import type { MechanicDefinition } from "./types";

const LANGUAGE_OPTIONS = [
  { value: "auto", label: "Auto (match player's UI language)" },
  { value: "en", label: "English" },
  { value: "he", label: "עברית (Hebrew)" },
  { value: "ru", label: "Русский (Russian)" },
  { value: "es", label: "Español (Spanish)" },
  { value: "fr", label: "Français (French)" },
  { value: "de", label: "Deutsch (German)" },
  { value: "pt", label: "Português (Portuguese)" },
  { value: "ja", label: "日本語 (Japanese)" },
  { value: "zh", label: "中文 (Chinese)" },
  { value: "ko", label: "한국어 (Korean)" },
  { value: "ar", label: "العربية (Arabic)" },
];

export const multilingual: MechanicDefinition = {
  id: "multilingual",
  name: "Player Language Override",
  description: "Override narration language to match the player's preferred language, regardless of the world's content language. The world's base language is always respected automatically — this mechanic is for when a player wants to play in a different language than the world was written in.",
  icon: "Languages",
  category: "narrative",
  auditPasses: 1,
  aiHints: {
    purpose: "Overrides narration language to the player's preferred language. The world's base language is injected automatically — this mechanic adds a PLAYER-SIDE override on top.",
    whenToUse: "When a player wants to play in a different language than the world's content language (e.g., play a Hebrew world in English).",
    synergies: ["narrator_style"],
    conflicts: [],
    prerequisites: [],
    tags: ["language", "translation", "multilingual", "narrator"],
    complexity: 1,
    exampleConfig: { language: "auto" },
    stateVariables: [],
  },
  configSchema: [
    {
      key: "language",
      label: "Narration Language",
      type: "select",
      options: LANGUAGE_OPTIONS,
      default: "auto",
    },
    {
      key: "keep_proper_nouns",
      label: "Keep Proper Nouns in Original Language",
      type: "select",
      options: [
        { value: "yes", label: "Yes — preserve character/place names untranslated" },
        { value: "no", label: "No — translate everything" },
      ],
      default: "yes",
    },
  ],
  systemPromptSection: (config) => {
    const lang = config.language || "auto";
    const keepNames = config.keep_proper_nouns !== "no";
    const worldLang = config._world_language;

    if (lang === "auto") {
      // When auto, the server injects the actual locale from the player's request
      const playerLocale = config._player_locale || "en";
      // If player locale matches world language, this mechanic is a no-op
      // (the world language instruction is already injected by the engine)
      if (worldLang && playerLocale === worldLang) return "";
      return buildLangPrompt(playerLocale, keepNames);
    }
    // If explicitly set language matches world language, no-op
    if (worldLang && lang === worldLang) return "";
    return buildLangPrompt(lang, keepNames);
  },
  tools: [],
};

function buildLangPrompt(lang: string, keepNames: boolean): string {
  const LANG_NAMES: Record<string, string> = {
    en: "English",
    he: "Hebrew (עברית)",
    ru: "Russian (Русский)",
    es: "Spanish (Español)",
    fr: "French (Français)",
    de: "German (Deutsch)",
    pt: "Portuguese (Português)",
    ja: "Japanese (日本語)",
    zh: "Chinese (中文)",
    ko: "Korean (한국어)",
    ar: "Arabic (العربية)",
  };

  const langName = LANG_NAMES[lang] || lang;
  const namesRule = keepNames
    ? "Keep proper nouns (character names, place names, item names) in their original form — do NOT translate them."
    : "Translate everything including proper nouns to sound natural in the target language.";

  return `## PLAYER LANGUAGE OVERRIDE
**CRITICAL**: The PLAYER prefers **${langName}**. ALL narration, descriptions, and story text MUST be written in **${langName}** (overriding the world's default language).
This applies to every response — narrator text, action descriptions, journal entries, quest descriptions, and all narrative output.
${namesRule}
Tool call arguments (function parameters) should remain in English for system compatibility.
The player's messages may come in any language — always respond in ${langName}.

**EXCEPTION — IN-WORLD FOREIGN DIALOGUE**: When an NPC speaks a language that is foreign WITHIN THE STORY WORLD (e.g. Aramaic, Latin, ancient Hebrew in a historical setting), write that dialogue in its original language WITHOUT translating it. The narration around the dialogue should still be in ${langName}, but the spoken words stay authentic. Do NOT add parenthetical translations. If the player character doesn't understand, narrate confusion — never translate for the reader.`;
}
